/**
 * Retire the inline boot screen once the app has actually painted.
 *
 * The screen itself lives in index.html so it can paint before any script or stylesheet
 * arrives; this is the other half, and it is deliberately small and defensive. It runs at
 * the boundary between "the document" and "the app", where a thrown error would leave the
 * user staring at a splash covering a working application, which is worse than the black
 * screen it replaced. So nothing here may throw, and every step is optional.
 */

const BOOT_ID = "nuvra-boot";
const READY_CLASS = "app-ready";
/** Matches the CSS fade in index.html. Removal after it keeps the DOM and a11y tree clean. */
const FADE_MS = 300;

/**
 * Mark the app painted: fades the boot screen out, then removes it.
 *
 * Idempotent, and safe to call when the element was never there (a host page that does not
 * include it, or a test environment). Returns true when a boot screen was actually retired,
 * so a caller can tell "removed it" from "there was nothing to remove" rather than assuming.
 */
export function markAppReady(doc: Document = document): boolean {
  try {
    doc.documentElement.classList.add(READY_CLASS);
    const el = doc.getElementById(BOOT_ID);
    if (!el) return false;
    // The class alone hides it; removal is the tidy-up, and is harmless if it never runs.
    setTimeout(() => { try { el.remove(); } catch { /* already gone */ } }, FADE_MS);
    return true;
  } catch {
    // A hostile or partial DOM must never stop the app from running.
    return false;
  }
}

/** True once the app has declared itself painted. Used by the stall notice in index.html. */
export function isAppReady(doc: Document = document): boolean {
  try {
    return doc.documentElement.classList.contains(READY_CLASS);
  } catch {
    return false;
  }
}
